/**
 * BillingSidebar
 * Responsabilidade: Exibir informações de faturamento na sidebar —
 * próxima fatura, método de pagamento, contato e detalhes.
 * Usa tokens semânticos do design system (sem gray-* hardcoded).
 */
import React from 'react';
import { Pencil } from 'lucide-react';
import { StripeBillingInfo } from '../../../services/modules/billing/billing.api';
import { Invoice } from '../../../services/modules/billing/invoices.service';
import { Card } from '../../../components/ui/Card';
import { formatCurrency, formatDateLong, formatCardBrand } from '../../../utils/formatters';

export interface BillingSidebarProps {
    billingInfo: StripeBillingInfo | null;
    billingContact: { name: string; email: string; initials: string } | null;
    hasActiveSubscription: boolean;
    onOpenPortal: () => void;
    isTrialing?: boolean;
    latestInvoice?: Invoice | null;
}

/** Label de seção reutilizável — uppercase tracking-widest */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="text-[10px] font-bold text-secondary uppercase tracking-widest">
        {children}
    </p>
);

/** Separador horizontal interno ao card */
const Divider: React.FC = () => (
    <div className="border-t border-border" aria-hidden="true" />
);

export const BillingInvoicesCard: React.FC<BillingSidebarProps> = ({
    billingInfo,
    isTrialing = false,
    latestInvoice,
    onOpenPortal,
}) => {
    const { subscription, upcomingInvoice } = billingInfo || {};

    return (
        <section aria-label="Informações de fatura" className="h-full w-full">
            <Card className="h-full flex flex-col">
                {/* Próxima Fatura */}
                <div className="p-5 flex flex-col gap-3">
                    <SectionLabel>
                        {isTrialing ? 'Faturas' : 'Próxima Fatura Estimada'}
                    </SectionLabel>

                    {isTrialing ? (
                        <p className="text-sm text-secondary leading-relaxed">
                            Sua primeira fatura vai aparecer aqui após o primeiro pagamento.
                        </p>
                    ) : upcomingInvoice ? (
                        <div className="flex flex-col gap-1">
                            <p className="text-2xl font-bold text-default leading-tight">
                                {formatDateLong(subscription?.currentPeriodEnd || upcomingInvoice.dueDate)}
                            </p>
                            <p className="text-2xl font-bold text-default leading-tight">
                                {formatCurrency(upcomingInvoice.amountDue / 100, upcomingInvoice.currency)}
                            </p>
                            <p className="text-xs text-secondary mt-1 leading-relaxed">
                                O preço não inclui impostos. Ofertas aplicadas antes do imposto.
                            </p>
                        </div>
                    ) : (
                        <p className="text-sm text-secondary">Nenhuma fatura pendente</p>
                    )}
                </div>

                {/* Última Fatura — oculta no trial */}
                {!isTrialing && (
                    <div className="mt-auto">
                        <Divider />
                        <div className="p-5 flex flex-col gap-3">
                            <SectionLabel>
                                {latestInvoice?.number
                                    ? `Última fatura: #${latestInvoice.number}`
                                    : 'Última fatura'}
                            </SectionLabel>

                            {latestInvoice ? (
                                <div className="flex flex-col gap-3">
                                    <dl className="flex items-center gap-3 text-sm">
                                        <dt className="sr-only">Data</dt>
                                        <dd className="text-secondary">
                                            {formatDateLong(latestInvoice.paid_at)}
                                        </dd>
                                        <div className="h-4 w-px bg-border" aria-hidden="true" />
                                        <dt className="sr-only">Valor</dt>
                                        <dd className="font-semibold text-default">
                                            {formatCurrency(latestInvoice.amount_paid / 100, latestInvoice.currency)}
                                        </dd>
                                    </dl>
                                    <button
                                        onClick={onOpenPortal}
                                        className="self-start text-sm font-semibold text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
                                    >
                                        Histórico de faturas
                                    </button>
                                </div>
                            ) : (
                                <p className="text-sm text-secondary">Nenhuma fatura encontrada</p>
                            )}
                        </div>
                    </div>
                )}
            </Card>
        </section>
    );
};

export const BillingDetailsCard: React.FC<BillingSidebarProps> = ({
    billingInfo,
    billingContact,
    hasActiveSubscription,
    onOpenPortal,
}) => {
    const { paymentMethod, customer } = billingInfo || {};
    const showEditAction = hasActiveSubscription;

    return (
        <section aria-label="Dados de pagamento e contato" className="h-full w-full">
            <Card className="h-full flex flex-col">
                {/* Método de Pagamento */}
                <div className="p-5 flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                        <SectionLabel>Método de Pagamento</SectionLabel>
                        {showEditAction && (
                            <button
                                onClick={onOpenPortal}
                                className="p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-primary-subtle transition-colors"
                                title="Editar método de pagamento"
                                aria-label="Editar método de pagamento"
                            >
                                <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                            </button>
                        )}
                    </div>

                    {paymentMethod ? (
                        <dl className="flex flex-col gap-0.5">
                            <dt className="sr-only">Cartão</dt>
                            <dd className="font-semibold text-default text-sm">
                                {formatCardBrand(paymentMethod.brand)} **** {paymentMethod.last4}
                            </dd>
                            <dt className="sr-only">Validade</dt>
                            <dd className="text-xs text-secondary">
                                Expira {paymentMethod.expMonth?.toString().padStart(2, '0')}/{paymentMethod.expYear}
                            </dd>
                        </dl>
                    ) : (
                        <p className="text-sm text-secondary">Nenhum método cadastrado</p>
                    )}
                </div>

                <Divider />

                {/* Contato de Faturamento */}
                <div className="p-5 flex flex-col gap-3">
                    <SectionLabel>Contato de Faturamento</SectionLabel>
                    <div className="flex items-center gap-3">
                        <div
                            className="w-9 h-9 rounded-full bg-primary-subtle text-primary flex items-center justify-center text-sm font-bold shrink-0"
                            aria-hidden="true"
                        >
                            {billingContact?.initials || 'U'}
                        </div>
                        <dl className="min-w-0">
                            <dt className="sr-only">Nome</dt>
                            <dd className="font-semibold text-default text-sm truncate">
                                {customer?.name || billingContact?.name || 'Usuário'}
                            </dd>
                            <dt className="sr-only">Email</dt>
                            <dd className="text-xs text-secondary truncate">
                                {customer?.email || billingContact?.email || 'email@exemplo.com'}
                            </dd>
                        </dl>
                    </div>
                </div>

                <Divider />

                {/* Detalhes da Fatura / Endereço */}
                <div className="p-5 flex flex-col gap-3">
                    <SectionLabel>Detalhes da Fatura</SectionLabel>

                    {customer?.address ? (
                        <address className="not-italic flex flex-col gap-0.5 text-sm text-default">
                            {customer.address.line1 && <span>{customer.address.line1}</span>}
                            {customer.address.line2 && <span>{customer.address.line2}</span>}
                            {(customer.address.city || customer.address.state || customer.address.postal_code) && (
                                <span>
                                    {[
                                        customer.address.city,
                                        customer.address.state,
                                        customer.address.postal_code,
                                    ].filter(Boolean).join(', ')}
                                </span>
                            )}
                            {customer.address.country && (
                                <span className="text-secondary">{customer.address.country}</span>
                            )}
                        </address>
                    ) : (
                        <p className="text-sm text-secondary">Nenhum endereço cadastrado</p>
                    )}
                </div>
            </Card>
        </section>
    );
};
